import { useCallback, useMemo, useState } from 'react';

import {
  isVisibleGuardianNotification,
  mapGuardianNotification,
} from '@/features/guardian/notifications/constants';
import type {
  GuardianNotification,
  GuardianNotificationFilter,
} from '@/features/guardian/notifications/types';
import {
  useDeleteGuardianNotificationMutation,
  useGuardianNotificationsQuery,
  useMarkAllGuardianNotificationsReadMutation,
  useMarkGuardianNotificationReadMutation,
} from '@/features/guardian/queries/notifications';
import { routeNotification } from '@/libs/notifications/notification-router';

type UseGuardianNotificationsOptions = {
  /** Return true when Event Detail handled the tap; false falls back to notification routing. */
  onEventNotificationPress?: (item: GuardianNotification) => boolean;
};

export function useGuardianNotifications(options: UseGuardianNotificationsOptions = {}) {
  const { onEventNotificationPress } = options;
  const [filter, setFilter] = useState<GuardianNotificationFilter>('all');
  const { data, refetch, fetchNextPage, hasNextPage, isRefetching, isFetchingNextPage } =
    useGuardianNotificationsQuery();
  const { mutate: markAsRead } = useMarkGuardianNotificationReadMutation();
  const { mutate: markAllAsRead } = useMarkAllGuardianNotificationsReadMutation();
  const { mutate: deleteNotification } = useDeleteGuardianNotificationMutation();

  const notifications = useMemo(() => {
    const uniqueNotifications = new Map(
      (data?.pages ?? [])
        .flatMap(page => page.notifications)
        .filter(isVisibleGuardianNotification)
        .map(item => [item.id, item]),
    );

    return Array.from(uniqueNotifications.values())
      .map(mapGuardianNotification)
      .sort((a, b) => b.receivedAt - a.receivedAt);
  }, [data?.pages]);

  const filteredNotifications = useMemo(
    () => notifications.filter(item => filter === 'all' || item.type === filter),
    [filter, notifications],
  );
  const unreadCount = useMemo(() => {
    if (filter !== 'all') return filteredNotifications.filter(item => !item.isRead).length;

    const apiUnreadCount = data?.pages[0]?.unreadCount ?? 0;
    const loadedUnreadShopDeals = (data?.pages ?? [])
      .flatMap(page => page.notifications)
      .filter(item => item.category === 'shopDeals' && !item.read).length;
    return Math.max(0, apiUnreadCount - loadedUnreadShopDeals);
  }, [data?.pages, filter, filteredNotifications]);

  const handleNotificationPress = useCallback(
    (item: GuardianNotification) => {
      if (!item.isRead) markAsRead(item.id);
      if (onEventNotificationPress?.(item)) return;

      routeNotification(
        {
          notificationId: item.id,
          type: item.apiType,
          destination: item.destination ?? undefined,
          playerId: item.playerId ?? undefined,
          sessionId: item.sessionId ?? undefined,
          bookingId: item.bookingId ?? undefined,
          invitationToken: item.invitationToken ?? undefined,
        },
        'tap',
        { markAsRead: false },
      );
    },
    [markAsRead, onEventNotificationPress],
  );
  const handleMarkAllRead = useCallback(() => markAllAsRead(), [markAllAsRead]);
  const handleDelete = useCallback((id: string) => deleteNotification(id), [deleteNotification]);
  const handleRefresh = useCallback(() => refetch(), [refetch]);
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return {
    filter,
    setFilter,
    notifications: filteredNotifications,
    totalNotificationCount: notifications.length,
    unreadCount,
    isReady: Boolean(data),
    isRefreshing: isRefetching && !isFetchingNextPage,
    isFetchingNextPage,
    handleNotificationPress,
    handleMarkAllRead,
    handleDelete,
    handleRefresh,
    handleLoadMore,
  };
}

export function useGuardianNotificationBadge() {
  const query = useGuardianNotificationsQuery();
  const apiUnreadCount = query.data?.pages[0]?.unreadCount ?? 0;
  const loadedUnreadShopDeals = (query.data?.pages ?? [])
    .flatMap(page => page.notifications)
    .filter(item => item.category === 'shopDeals' && !item.read).length;
  return apiUnreadCount - loadedUnreadShopDeals > 0;
}
